/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { loc } from "../../../../core/utils"
import { DATE_FORMAT } from "../../constants/identificationPageConstants"
import genericSchema from "../IdentificationPageValidator"
import CodiceFiscale from "codice-fiscale-js"
import emojiRegex from "emoji-regex"
import moment from "moment"
import * as yup from "yup"

/* regex */
const phoneNumberRegex = /^393\d{8,9}$/
export const selectNotCorrectValue = () => loc`Please pick a correct value`
const specialCharaterRegex = /[!@#$%^&*()+={}':"\\|<>/?]+/

const emailRegex = /^[a-zA-Z0-9_\-.]+@[a-zA-Z0-9_\-.]+\.[a-zA-Z0-9_\-.]+$/
const defaultRegex =
  /^[a-zA-ZêëéèçÊËÉÈÇàâäÀÂ' ÄùûüÙÛÜìïîÌÏÎòôöÒÔÖ]+(([ -][a-zA-Z êëéèçÊËÉÈÇàâäÀÂÄùûüÙÛÜìïîÌÏÎòôöÒÔÖ])?[a-zA-ZêëéèçÊËÉÈÇàâäÀÂÄùûüÙÛÜìïîÌÏÎòôöÒÔÖ]*)*$/

/* error messages */
const requiredMessage = () => loc`Required`
const taxNumberInvalidMessage = () => loc`Not a valid tax number`
const maximumCharactersMessage = (maximumValue) => maximumValue + " " + loc`maximum characters`
const phoneNumberInvalidMessage = () => loc`Invalid phone number`
const emailNotValidMessage = () => loc`Email invalid`
const invalideDateErrorMessage = () => loc`Not a valid date`
const birthDateMinMessage = () => loc`Minimum age must be > 18 years old`
const birthDateMaxessage = () => loc`Maximum age must be < 110 years old`
const regexMessage = () => loc`Unauthorized characters`
const unauthorizedCharactersMessage = () => loc`Unauthorized characters`

let schema = yup.object().shape({
  identity: yup.object().shape({
    name: yup.string().nullable().required(requiredMessage).max(25, maximumCharactersMessage(25)).matches(defaultRegex, unauthorizedCharactersMessage()),
    firstName: yup.string().nullable().max(25, maximumCharactersMessage(25)).required(requiredMessage).matches(defaultRegex, unauthorizedCharactersMessage()),
    taxNumber: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "taxNumber",
        message: "",
        test(value) {
          try {
            let codiceFiscale = new CodiceFiscale(value)
            let birthDate = this.options.context.identity.birthDate.split("/")
            var cf = new CodiceFiscale({
              // first name ( prenom ) et name ( nom de famille)
              name: this.options.context.identity.firstName,
              surname: this.options.context.identity.name,
              gender: getTitleFromEnum(this.options.context.identity.title),
              day: birthDate[0],
              month: birthDate[1],
              year: birthDate[2],
              //unused value for checking but mandatory field for instance
              birthplace: "Napoli"
            })
            if (codiceFiscale.code.substring(0, 11) !== cf.code.substring(0, 11)) {
              return this.createError({
                message: taxNumberInvalidMessage,
                path: "identity.taxNumber"
              })
            }
          } catch (error) {
            return this.createError({
              message: taxNumberInvalidMessage,
              path: "identity.taxNumber"
            })
          }
        }
      }),
    birthDate: yup
      .string()
      .nullable()
      .test({
        name: "ageTest",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "identity.birthDate"
            })
          }
          const birthDate = moment(value, DATE_FORMAT.identity.birthDate.IT)
          if (!birthDate.isValid() || birthDate.year() < 1000 || birthDate.year() > 9999) {
            return this.createError({
              message: invalideDateErrorMessage,
              path: "identity.birthDate"
            })
          }
          let currentDate = moment()
          if (!(currentDate.diff(birthDate, "years") >= 18)) {
            return this.createError({
              message: birthDateMinMessage,
              path: "identity.birthDate"
            })
          }
          if (!(currentDate.diff(birthDate, "years") < 110)) {
            return this.createError({
              message: birthDateMaxessage,
              path: "identity.birthDate"
            })
          }
        }
      }),
    birthCountry: yup.string().nullable().required(requiredMessage),
    birthDepartment: yup.string().nullable().required(requiredMessage),
    birthCity: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "birthCity verification",
        message: "",
        test(value) {
          const regex = emojiRegex()
          if (!value) {
            return this.createError({
              message: selectNotCorrectValue,
              path: "identity.birthCity"
            })
          }
          if (specialCharaterRegex.test(value)) {
            return this.createError({
              message: regexMessage,
              path: "identity.birthCity"
            })
          }
          if (value?.match(regex)) {
            return this.createError({
              message: regexMessage,
              path: "identity.birthCity"
            })
          }
        }
      })
  }),
  coordinates: yup.object().shape({
    phoneNumber: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "phoneNumber",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.phoneNumber"
            })
          }
          if (!value.replace("+", "").match(phoneNumberRegex)) {
            return this.createError({
              message: phoneNumberInvalidMessage,
              path: "coordinates.phoneNumber"
            })
          }
        }
      }),
    email: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "email",
        message: "",
        test(value) {
          if (!value?.match(emailRegex)) {
            return this.createError({
              message: emailNotValidMessage,
              path: "coordinates.email"
            })
          }
        }
      })
  })
})

const getTitleFromEnum = (numero) => {
  if (numero === "1") {
    return "M"
  }
  return "F"
}
export default (values) => {
  return schema.concat(genericSchema).validate(values, { abortEarly: false, context: values })
}
